import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Input } from "@/components/Input";
import { roleManagementApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import type { RoleItem } from "@/pages/Roles/types";

interface EditRoleModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (role: RoleItem) => void;
    role: RoleItem;
}
export const EditRoleModal = ({
    isOpen,
    onClose,
    onSuccess,
    role,
}: EditRoleModalProps) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();

    const [tag, setTag] = useState("");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");

    const [loading, setLoading] = useState(false);

    const [tagError, setTagError] = useState(false);
    const [nameError, setNameError] = useState(false);

    const tagRef = useRef<HTMLDivElement>(null);
    const nameRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen && role) {
            setTag(role.tag || "");
            setName(role.name || "");
            setDescription(role.description || "");
            setTagError(false);
            setNameError(false);
        }
    }, [isOpen, role]);

    const handleSubmit = async () => {
        const missingTag = !tag.trim();
        const missingName = !name.trim();

        if (missingTag || missingName) {
            setTagError(missingTag);
            setNameError(missingName);

            showError(t("developments.modal_new.validation_required"));

            const firstMissing = missingTag ? tagRef : nameRef;
            firstMissing.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });

            return;
        }

        setLoading(true);
        try {
            const res = await roleManagementApi.update(role.id, {
                tag,
                name,
                description,
            });
            showSuccess(t("roles.updated"));
            onSuccess(res.data);
            onClose();
        } catch (error) {
            showError(
                error instanceof Error ? error.message : t("roles.error_update")
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("roles.edit_title")}
            onSubmit={handleSubmit}
            submitLabel={t("common.save")}
            submittingLabel={t("common.saving")}
            cancelLabel={t("common.cancel")}
            isSubmitting={loading}
            maxWidth="md"
        >
            <div className="space-y-4 p-4">
                <div ref={tagRef}>
                    <Input
                        label={t("roles.tag")}
                        value={tag}
                        onChange={(e) => {
                            setTag(e.target.value);
                            if (tagError) setTagError(false);
                        }}
                        placeholder="Ej. admin, client"
                        required
                        className={
                            tagError ? "animate-error-blink rounded-xl" : ""
                        }
                    />
                </div>
                <div ref={nameRef}>
                    <Input
                        label={t("roles.name")}
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            if (nameError) setNameError(false);
                        }}
                        required
                        className={
                            nameError ? "animate-error-blink rounded-xl" : ""
                        }
                    />
                </div>
                <Input
                    label={t("roles.description")}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
            </div>
        </Modal>
    );
};
